
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getRichTextHtml } from '@msdyn365-commerce/core';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ICittaSubscribeNewsletterFormData } from './citta-subscribe-newsletter-form.data';
import { ICittaSubscribeNewsletterFormProps } from './citta-subscribe-newsletter-form.props.autogenerated';

interface ICittaSubscribeNewsletterFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    privacyPolicyChecked: boolean;
    eAValError: boolean;
    eAError: boolean;
    fNError: boolean;
    lNError: boolean;
    tsCsError: boolean;
    rCCError: boolean;
    reqError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    responseSuccess: boolean;
    charactersLeft?: number;
    firstNameErrorClass: string;
    lastNameErrorClass: string;
    emailErrorClass: string;
    privacyPolicyErrorClass: string;
    reCaptchaChecked: boolean;
}

export interface ICittaSubscribeNewsletterForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    firstAndLastNameInput: React.ReactNode;
    emailInput: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    subscribeButtton: React.ReactNode;
    subscribeThanks: React.ReactNode;
    tsAndCsText: React.ReactNode;
    reCaptcha: React.ReactNode;
}


export interface ICittaSubscribeNewsletterFormViewProps extends ICittaSubscribeNewsletterFormProps<ICittaSubscribeNewsletterFormData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaSubscribeNewsletterForm;
    responseSuccess: boolean;
    formErrors: React.ReactNode;
    formErrorsNodeProps: INodeProps;
    successResponse: React.ReactNode;
    showSuccessResponse: boolean;
    successResponseNodeProps: INodeProps;
}

/**
 *
 * CittaInStoreGiveawayForm component
 * @extends {React.PureComponent<ICCittaInStoreGiveawayFormProps<ICittaInStoreGiveawayFormData>>}
 */
class CittaSubscribeNewsletterForm extends React.PureComponent<ICittaSubscribeNewsletterFormProps<ICittaSubscribeNewsletterFormData>, ICittaSubscribeNewsletterFormState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;

    private _recaptchaRef: React.RefObject<ReCAPTCHA>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;

    // private phoneNumberValue: boolean = false;
    private formInputValues: boolean = false;
    private responseMessage: string | undefined;
    private moduleClassName: string = 'newsletterSubWrap';
    private recaptchaErrorMessage: string | undefined;

    constructor(props: ICittaSubscribeNewsletterFormProps<ICittaSubscribeNewsletterFormData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubmit = this._handleSubmit.bind(this);

        this.state = {
            firstName: '',
            lastName: '',
            emailAddress: '',
            privacyPolicyChecked: false,
            fNError: false,
            lNError: false,
            eAValError: false,
            eAError: false,
            tsCsError: false,
            rCCError: false,
            reqError: false,
            responseReceived: false,
            responseError: false,
            responseSuccess: false,
            firstNameErrorClass: 'hide',
            lastNameErrorClass: 'hide',
            emailErrorClass: 'hide',
            privacyPolicyErrorClass: 'hide',
            reCaptchaChecked: false
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaSubscribeNewsletterFormViewProps = {
            ...(this.props as ICittaSubscribeNewsletterFormProps<ICittaSubscribeNewsletterFormData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName)
            },
            subscribeResponse: this._getSubmitResponse(),
            subscribeForm: this._renderForm(),
            responseSuccess: this.state.responseSuccess,
            formErrors: this._renderErrors(),
            formErrorsNodeProps: {
                className: 'competition-form-errors'
            },
            successResponse: this._renderSuccessResponse(),
            showSuccessResponse: this.state.responseSuccess,
            successResponseNodeProps: {
                className: 'competition-form-success'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSubmitResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }

    private _renderSuccessResponse(): React.ReactNode {
        return (
            this.state.responseSuccess
                ? (
                    <div className="response-succcess">
                        <h2>{this.props.config.successTextResponse}</h2>
                    </div>
                )
                : null
        );
    }

    private _renderForm(): ICittaSubscribeNewsletterForm {
        return {
            FormWrapper: {
                className: 'competition-form',
            },
            FormContainer: {
                className: 'competition-form-div',
            },
            firstAndLastNameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            subscribeButtton: this._renderSubmitButton(),
            subscribeThanks: this._renderSubmitThanks(),
            tsAndCsText: this._renderTsCsText(),
            reCaptcha: this._renderReCaptcha()
        };
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderReCaptcha(): React.ReactNode {
        //recaptcha newsletter-recapture-visable
        return (
            <div>
                {
                    this.props.config.reCaptchaOption ? <div className=''>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}

                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            size="invisible"
                            onChange={() => this._submit()}
                        />
                    </div> : null
                }
            </div>
        );
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='name-input-container'>
                <div className='name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.config.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='First Name'
                        className='name-input-text'
                        placeholder={this.props.config.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderTsCsText(): React.ReactNode {
        return (
            <div className='terms-and-conditions-richtext'>
                <span dangerouslySetInnerHTML={getRichTextHtml(this.props.config.privacyPolicyText)} />
            </div>
        );
    }

    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    value={this.state.emailAddress}
                    required
                    placeholder={this.props.config.emailInputPlaceholder}
                />
            </div>
        );
    }

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>{this.props.config.privacyPolicyText}<a href={this.props.config.privacyPolicyLink}>Privacy Policy</a></p>
                    </span>
                </div>
            </div>
        );
    }

    private _renderSubmitButton(): React.ReactNode {
        return (
            <div className="subscribe-button-container">
                <Button
                    className={`subscribe-button btn-thirdary`}
                    title={this.props.config.submitButtonLabel}
                    color='primary'
                    onClick={this._handleSubmit}
                >{this.props.config.submitButtonLabel}
                </Button>
            </div>
        );
    }

    private _renderSubmitThanks(): React.ReactNode {
        return (
            <div>
                <div className={`subscribe-thanks`}>
                    Thanks for Entering!
                </div>
            </div>
        );
    }

    private _showErrors = (): void => {
        this.setState({
            emailErrorClass: this._checkEmailError() ? 'show' : 'hide',
            firstNameErrorClass: (this._FirstNameInputRef.current && this._FirstNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            lastNameErrorClass: (this._LastNameInputRef.current && this._LastNameInputRef.current.value.length === 0) ? 'show' : 'hide',
            privacyPolicyErrorClass: this._PrivacyPolicyRef.current!.checked ? 'hide' : 'show'
        });
    }

    private _checkEmailError = (): boolean => {
        const email: string = this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value || '';
        if (email.length === 0) {
            return true;
        } else {
            return !this._isEmailValid(email);
        }
    }

    private _renderErrors = (): JSX.Element => {
        const {resources} = this.props;
        return (
            <>
                <div className={this.state.firstNameErrorClass}>
                    {resources.firstNameError}
                </div>
                <div className={this.state.lastNameErrorClass}>
                    {resources.lastNameError}
                </div>
                <div className={this.state.emailErrorClass}>
                    {resources.emailError}
                </div>
                <div className={this.state.privacyPolicyErrorClass}>
                    {resources.ppError}
                </div>
            </>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, fNError: false, reqError: false, eAValError: false });
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, lNError: false, reqError: false, eAValError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false });
                break;
            }
            case 'privacyPolicy': {
                const isChecked = this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked ? this._PrivacyPolicyRef.current.checked : false;
                this.setState({ privacyPolicyChecked: isChecked, reqError: false, eAValError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false });
            }
        }
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private async _handleSubmit(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        await this._recaptchaRef.current?.execute()
    }

    private async _submit(): Promise<void> {
        this._showErrors();

        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const eAVal = this._EmailAddressInputRef.current!.value;
        const recaptchaValue = this._recaptchaRef.current?.getValue();
        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
         } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
            this._recaptchaRef.current?.reset();
        } else if (!this._PrivacyPolicyRef.current!.checked) {
            this.setState({
                privacyPolicyChecked: false,
                tsCsError: true
            });
            this._recaptchaRef.current?.reset();
        }
        else if (!recaptchaValue) {
            this.setState({
                rCCError: true
            });
            this._recaptchaRef.current?.reset();
        } else {
            this.formInputValues = (this.emailAddressValue) ? true : false;
            if (this.formInputValues && !this.state.eAValError) {
                try {
                    await this._postEntry(fN, lN, eA);

                    this._clearValues();
                    setTimeout(() => {
                      window.location.reload();
                    }, 6000);
                } catch (e) {
                    console.log(e, 'Please fill in the form!');
                }
            }
        }
    }

    private async _postEntry(firstName: string, lastName: string, email: string): Promise<void> {
        // NB in test the post body will need to look like this: { content: { emailAddress: email } }
        /* tslint:disable */
        let emarsysCompetitionGroupId = this.props.config.emarsysCompetitionGroupId;
        let emarsysGroupName = this.props.config.emarsysGroupOptInName;

        // NB in test the post body will need to look like this: { content: { emailAddress: email } }

        /* tslint:disable */
        const response = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostContact',
            'post',
            {
                firstName: firstName,
                lastName: lastName,
                emailAddress: email,
                country: this.props.config.country,
                emarsysGroup: this.props.config.emarsysGroupId,
                emarsysCompetitionGroupId: emarsysCompetitionGroupId,
                emarsysGroupName: emarsysGroupName,
            }

        ) as any;

        this.responseMessage = response?.data.Message;

        if(response?.data.Result !== 'Error') {

            if (MsDyn365.msdyn365Commerce.isBrowser && this.props.config.urlToOpenOnSuccess) {
                // @ts-ignore
                // window.location = this.props.config.urlToOpenOnSuccess;
                window.open(this.props.config.urlToOpenOnSuccess, '_blank');
            }

        this.setState({
            responseReceived: true,
            responseError: response?.data.Result === 'Error',
            responseSuccess: response?.data.Result !== 'Error',
            emailAddress: '',
            firstName: '',
            lastName: '',
            reqError: false,
            eAValError: false,
            rCCError: false
        });
        /* tslint:enable */

    } else {
        this.setState({
            responseReceived: true,
            responseError: response?.data.Result === 'Error',
            responseSuccess: response?.data.Result !== 'Error',
            emailAddress: '',
            firstName: '',
            lastName: '',
            reqError: false,
            eAValError: false,
            rCCError: false
        });
        
    }

    }

    private _clearValues(): void {
        this._recaptchaRef.current?.reset();
        if (this._FirstNameInputRef.current) {
            this._FirstNameInputRef.current!.value = '';
        }
        if (this._LastNameInputRef.current) {
            this._LastNameInputRef.current!.value = '';
        }
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
        }
        if (this._EmailAddressInputRef.current) {
            this._EmailAddressInputRef.current!.value = '';
        }
    }

}

export default CittaSubscribeNewsletterForm;
